import styled, { css, DefaultTheme, keyframes } from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens";

// New-look styling for the onboarding steps, also shared with new-look
// screens elsewhere (auth hero, create-account drawer).

// E2E runs set this flag so tests never wait on (or flake because of) motion.
const MOTION_ENABLED = import.meta.env.VITE_E2E_DISABLE_ANT_MOTION !== "true";

export const ONBOARDING_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

/** Phones: the onboarding's side margin (header, back link, steps, content). */
export const ONBOARDING_PHONE_GUTTER = 20;
const REVEAL_DURATION_MS = 500;
const REVEAL_STAGGER_MS = 70;

// Mirrors the marketing site's fadeInUp. It only animates opacity/transform
// and ends on `transform: none`, so nothing is hidden or left transformed.
// Fill mode is `backwards`: the start state covers the stagger delay, and once
// finished the element's own styles (e.g. hover lift) apply again.
// The rise is a variable so a scope can drop it (onboarding on phones fades
// only: a native screen doesn't slide its content in).
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, var(--onboarding-reveal-rise, 20px), 0);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

type Styles = ReturnType<typeof css>;

// Motion styles apply only in the new look, when the user allows motion,
// and never in e2e runs.
export const withMotion = (styles: Styles): Styles =>
  MOTION_ENABLED
    ? css`
        ${(p) =>
          p.theme.isNewLook &&
          css`
            @media (prefers-reduced-motion: no-preference) {
              ${styles}
            }
          `}
      `
    : css``;

// Fades an element in from 20px below, delayed by its position in the step.
export const reveal = (order = 0): Styles =>
  withMotion(css`
    animation: ${fadeInUp} ${REVEAL_DURATION_MS}ms ${ONBOARDING_EASE}
      ${order * REVEAL_STAGGER_MS}ms backwards;
  `);

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
`;

// Fades an element in without moving it: for containers that hold a fixed
// bar (an ancestor's transform would pin the bar to it until the motion ends).
export const revealFade = (order = 0): Styles =>
  withMotion(css`
    animation: ${fadeIn} ${REVEAL_DURATION_MS}ms ${ONBOARDING_EASE}
      ${order * REVEAL_STAGGER_MS}ms backwards;
  `);

// Staggers the direct children of a container, starting at `start`.
export const revealChildren = (start = 0, count = 8): Styles =>
  withMotion(css`
    > * {
      animation: ${fadeInUp} ${REVEAL_DURATION_MS}ms ${ONBOARDING_EASE}
        backwards;
    }
    ${Array.from(
      { length: count },
      (_, index) => css`
        > *:nth-child(${index + 1}) {
          animation-delay: ${(start + index) * REVEAL_STAGGER_MS}ms;
        }
      `
    )}
    > *:nth-child(n + ${count + 1}) {
      animation-delay: ${(start + count) * REVEAL_STAGGER_MS}ms;
    }
  `);

// Instrument surfaces: panels sit on the canvas with a hairline and no
// shadow; shadows belong to floating layers only.

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

// Opaque blend of `overlay` over `base` (both #rrggbb), so it can transition.
const blendHex = (base: string, overlay: string, alpha: number): string => {
  if (!HEX_COLOR.test(base) || !HEX_COLOR.test(overlay))
    return hexToRGB(overlay, alpha) || base;
  const channel = (hex: string, index: number): number =>
    parseInt(hex.slice(1 + index * 2, 3 + index * 2), 16);
  const mixed = [0, 1, 2].map((index) =>
    Math.round(
      channel(base, index) * (1 - alpha) + channel(overlay, index) * alpha
    )
  );
  return `rgb(${mixed.join(", ")})`;
};

export const selectedTint = (theme: DefaultTheme): string =>
  blendHex(
    theme.colors.cardBgColor,
    theme.colors.primaryBlue,
    theme.isDarkMode ? 0.08 : 0.04
  );

// The inset well behind examples and supporting information.
export const softPanelBackground = (theme: DefaultTheme): string =>
  getNewLookTokens(theme).well ?? theme.colors.cardLightBgColor;

// A panel: white on the canvas, hairline, radius 12, no shadow.
export const cardSurface = css`
  box-sizing: border-box;
  background-color: ${(p) => p.theme.colors.cardBgColor};
  border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: none;
`;

// A well: the quieter inset surface (illustrations, supporting notes).
export const softPanel = css`
  box-sizing: border-box;
  background: ${(p) => softPanelBackground(p.theme)};
  border: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: none;
`;

// Clickable panels: the border answers hover (no lift, no shadow) and the
// keyboard focus ring sits outside the card.
export const interactiveSurface = css`
  cursor: pointer;
  ${withMotion(css`
    transition: border-color 150ms ease, box-shadow 150ms ease,
      background-color 150ms ease;
  `)}
  &:hover {
    border-color: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.45)};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 2px;
  }
`;

// Selected: a 2px accent border (1px border + 1px inset ring); the card's
// check says the rest.
export const selectedSurface = css`
  border-color: ${(p) => p.theme.colors.primaryBlue};
  box-shadow: inset 0 0 0 1px ${(p) => p.theme.colors.primaryBlue};
  &:hover {
    border-color: ${(p) => p.theme.colors.primaryBlue};
  }
`;

// Step title / description: the page title and body roles.
export const stepTitle = css`
  ${newLookType.pageTitle};
  color: ${(p) => p.theme.colors.titleColor};
`;

export const stepDescription = css`
  ${newLookType.body};
  color: ${(p) => p.theme.colors.secondaryText};
`;

// Small group labels above a title ("Step 1 of 2 · Trading method").
export const eyebrowText = css`
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;

export const ONBOARDING_ICON_PROPS = {
  size: 20,
  strokeWidth: 1.75,
  "aria-hidden": true,
} as const;

export const INLINE_ICON_PROPS = {
  size: 16,
  strokeWidth: 1.75,
  "aria-hidden": true,
} as const;

// Tinted square that holds a line icon (new look only).
export const IconTile = styled.span<{ $size?: number; $round?: boolean }>`
  flex: 0 0 auto;
  display: inline-grid;
  place-items: center;
  width: ${(p) => p.$size ?? 40}px;
  height: ${(p) => p.$size ?? 40}px;
  border-radius: ${(p) => (p.$round ? "50%" : "12px")};
  background: ${(p) => p.theme.colors.lightBlue};
  color: ${(p) => p.theme.colors.primaryBlue};
  svg {
    display: block;
  }
`;

// Round check used for selectable options (new look only).
export const SelectionCheck = styled.span<{ $selected: boolean }>`
  flex: 0 0 auto;
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1.5px solid
    ${(p) =>
      p.$selected
        ? p.theme.colors.primaryBlue
        : p.theme.colors.lightBorderGrey};
  background: ${(p) =>
    p.$selected ? p.theme.colors.primaryBlue : p.theme.colors.cardBgColor};
  color: ${(p) => p.theme.colors.buttonText};
  ${withMotion(css`
    transition: background-color 150ms ease, border-color 150ms ease;
  `)}
  svg {
    display: block;
  }
`;
